import { toast } from 'react-toastify';
import moment from 'moment';

import {
  fetchSingleTransaction,
  fetchTransaction,
  fetchResume,
  createTransaction,
  updateTransaction,
  updateSituation,
  fetchCashierDetails,
  fetchStoreCashierDetails,
  deleteTransaction,
  fetchWithdral,
  createAttachment,
  createCashierAttachment,
  deleteCashierAttachment
} from './services';

import {
  setTransaction,
  setTransactionContent,
  setResumeConfirmed,
  setResumePendent,
  setResumePendentIsLoading,
  handleLoading,
  handleRegistration,
  clearTransaction,
  setCashierDetails,
  handleDetailing,
  setAttachment
} from './actions';

import { handleExporting } from '../../../components/Filter/store/actions';
import { convertFormattedMoneyAndNegativeMoneyToNumber } from '../../../helpers/converters';
import { formatTransaction, buildUnifiedCashierClosingEnrichment } from '../../../helpers/formatters';

const enrichUnifiedCashierClosingTransactions = async (content, userId, establishmentId) => {
  return Promise.all(content.map(async item => {
    if(!item?.isUnifiedCashierClosing) {
      return item;
    }

    const cashierId = item.cashierId || item.sourceId;
    const itemEstablishmentId = item.establishmentId || establishmentId;

    if(!itemEstablishmentId || (!cashierId && !item.storeCashierId)) {
      return item;
    }

    try {
      const response = item.storeCashierId && !cashierId
        ? await fetchStoreCashierDetails(userId, itemEstablishmentId, item.storeCashierId)
        : await fetchCashierDetails(userId, itemEstablishmentId, cashierId);

      const { description, amount } = buildUnifiedCashierClosingEnrichment(response?.data);

      return {
        ...item,
        ...(description ? { description } : {}),
        ...(amount != null ? { amount } : {})
      }
    } catch(error) {
      return item;
    }
  }));
}

export const loadSingleTransaction = (userId, establishmentId, transactionId) => dispatch => {
  dispatch(handleLoading(true));

  return fetchSingleTransaction(userId, establishmentId, transactionId)
    .then(({ data }) => data)
    .catch(() => dispatch(handleLoading(false)))
    .finally(() => dispatch(handleLoading(false)));
}

export const loadTransaction = ({
  qp = '',
  page,
  extraProps,
  isCsv = false
}) => dispatch => {
  dispatch(handleLoading(true));

  const { userId, establishmentId, startDate, endDate, exportation } = extraProps;

  let url = !establishmentId
    ? `${userId}/financial/extract?startDate=${startDate}&endDate=${endDate}&page=${page}${qp}`
    : `${userId}/establishment/${establishmentId}/financial/extract?startDate=${startDate}&endDate=${endDate}&page=${page}${qp}`;

  if(exportation) {
    dispatch(handleExporting(true));

    return fetchTransaction(url)
      .then(({ data }) => {
        dispatch(handleExporting(false));
        dispatch(handleLoading(false));

        if(isCsv) {
          return data;
        }

        return Promise.resolve(data);
      })
      .catch(() => {
        dispatch(handleExporting(false));
        dispatch(handleLoading(false));
        return Promise.reject({});
      });
  }

  return fetchTransaction(url)
    .then(async ({ data }) => {
      dispatch(setTransaction(data));

      const content = formatTransaction(data);
      const hasUnifiedCashierClosings = content.some(item => item?.isUnifiedCashierClosing);

      if(hasUnifiedCashierClosings) {
        const enrichedContent = await enrichUnifiedCashierClosingTransactions(content, userId, establishmentId);
        dispatch(setTransactionContent(enrichedContent));
      }

      dispatch(handleLoading(false));
    })
    .catch(() => dispatch(handleLoading(false)));
}

export const loadResume = (userId, establishmentId, extraProps) => dispatch => {
  const { startDate, endDate, qp } = extraProps;

  const url = !establishmentId
    ? `${userId}/financial/resume/confirmed?startDate=${startDate}&endDate=${endDate}${qp}`
    : `${userId}/establishment/${establishmentId}/financial/resume/confirmed?startDate=${startDate}&endDate=${endDate}${qp}`;

  return fetchResume(url)
    .then(data => dispatch(setResumeConfirmed(data)));
}

export const loadResumePendent = (userId, establishmentId, extraProps) => dispatch => {
  dispatch(setResumePendentIsLoading(true));

  const { startDate, endDate, qp } = extraProps;

  const url = !establishmentId
    ? `${userId}/financial/resume/pendent?startDate=${startDate}&endDate=${endDate}${qp}`
    : `${userId}/establishment/${establishmentId}/financial/resume/pendent?startDate=${startDate}&endDate=${endDate}${qp}`;

  return fetchResume(url)
    .then(data => dispatch(setResumePendent(data)))
    .finally(() => dispatch(setResumePendentIsLoading(false)));
}

export const upsertTransaction = (
  params,
  extraProps,
  isChangingConfirmation = false,
  isChangingAmount = false
) => dispatch => {
  dispatch(handleLoading(true));

  const {
    isConfirm,
    transactionId,
    transactionTypeId,
    description,
    categoryId,
    accountId,
    amount,
    previsionDateTime,
    paymentDateTime,
    confirmationDateTime,
    paymentMethodId,
    attachment
  } = params;

  const amountConverted = convertFormattedMoneyAndNegativeMoneyToNumber(amount);
  let situationId = isConfirm ? 2 : 1;

  if (transactionTypeId === 2 && isConfirm) {
    situationId = 3;
  }

  const transactionDateTime = moment().format('YYYY-MM-DD HH:mm:ss');

  const formatPrevisionDateTime =
    previsionDateTime.split(':').length > 1
      ? previsionDateTime
      : `${previsionDateTime} 00:00:01`;

  let paramsConverted;

  if (isChangingConfirmation) {
    paramsConverted = {
      transactionId,
      confirmationDateTime
    }
  } else if (isChangingAmount) {
    paramsConverted = {
      transactionId,
      amount: amountConverted
    }
  } else {
    paramsConverted = {
      transactionId,
      transactionDateTime,
      description,
      categoryId,
      accountId,
      situationId,
      paymentMethodId,
      attachment,
      amount: amountConverted,
      previsionDateTime: formatPrevisionDateTime,
      paymentDateTime: isConfirm ? paymentDateTime : null,
      confirmationDateTime: transactionTypeId === 2 && isConfirm ? confirmationDateTime : null
    }
  }

  const { userId, establishmentId } = extraProps;

  if (!paramsConverted.transactionId) {
    return createTransaction(userId, establishmentId, paramsConverted)
      .then(() => {
        toast.success('Transação cadastrada com sucesso.');
        dispatch(handleRegistration(false, 'receipt'));
        dispatch(clearTransaction());
        dispatch(
          loadTransaction({
            page: extraProps.page,
            pageSize: extraProps.pageSize,
            extraProps: {
              userId,
              establishmentId,
              ...extraProps
            }
          })
        );
      })
      .catch(() => dispatch(handleLoading(false)));
  }

  return updateTransaction(userId, establishmentId, paramsConverted, paramsConverted.transactionId)
    .then(() => {
      toast.success('Transação atualizada com sucesso.');
      dispatch(handleRegistration(false, 'receipt'));
      dispatch(clearTransaction());
      dispatch(
        loadTransaction({
          page: extraProps.page,
          pageSize: extraProps.pageSize,
          extraProps: {
            userId,
            establishmentId,
            ...extraProps
          }
        })
      );
    })
    .catch(() => dispatch(handleLoading(false)));
}

export const changeStatus = (
  transactionId,
  situationId,
  extraProps
) => dispatch => {
  const params = {
    situationId: situationId === 2 ? 3 : 2
  }

  const { userId, establishmentId } = extraProps;

  return updateSituation(userId, establishmentId, transactionId, params)
    .then(() => {
      dispatch(
        loadTransaction({
          page: extraProps.page,
          pageSize: extraProps.pageSize,
          extraProps: {
            userId,
            establishmentId,
            ...extraProps
          }
        })
      );
      toast.success('Transação atualizada com sucesso!');
    })
    .catch(() => dispatch(handleLoading(false)));
}

export const loadCashierDetails = (userId, establishmentId, sourceId) => dispatch => {
  dispatch(handleDetailing(true));

  return fetchCashierDetails(userId, establishmentId, sourceId)
    .then(({ data }) => {
      dispatch(setCashierDetails(data));
      return data;
    })
    .finally(() => dispatch(handleDetailing(false)));
}

export const loadStoreCashierDetails = (userId, establishmentId, storeCashierId) => dispatch => {
  dispatch(handleDetailing(true));

  return fetchStoreCashierDetails(userId, establishmentId, storeCashierId)
    .then(({ data }) => {
      dispatch(setCashierDetails(data));
      return data;
    })
    .finally(() => dispatch(handleDetailing(false)));
}

export const removeTransaction = (params, extraProps) => dispatch => {
  const { transactionId } = params;

  const { userId, establishmentId } = extraProps;

  dispatch(handleLoading(true));

  return deleteTransaction(userId, establishmentId, transactionId, params)
    .then(() => {
      toast.success('Transação removida com sucesso.');
      dispatch(handleRegistration(false, 'receipt'));
      dispatch(clearTransaction());
      dispatch(
        loadTransaction({
          page: extraProps.page,
          pageSize: extraProps.pageSize,
          extraProps: {
            userId,
            establishmentId,
            ...extraProps
          }
        })
      );
    })
    .catch(() => dispatch(handleLoading(false)));
}

export const getWithdral = (userId, establishmentId, transactionId) => dispatch => {
  dispatch(handleDetailing(true));

  return fetchWithdral(userId, establishmentId, transactionId)
    .then(({ data }) => dispatch(setCashierDetails(data)))
    .finally(() => dispatch(handleDetailing(false)));
}

export const upsertAttachment = (userId, establishmentId, file) => dispatch => {
  return createAttachment(userId, establishmentId, file)
    .then(res => dispatch(setAttachment(res.data)));
}

export const upsertCashierAttachment = (userId, establishmentId, cashierId, file, column) => dispatch => {
  return createCashierAttachment(userId, establishmentId, cashierId, file, column);
}

export const removeCashierAttachment = (userId, establishmentId, cashierId, file) => dispatch => {
  return deleteCashierAttachment(userId, establishmentId, cashierId, file)
    .then(res => res);
}